import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

/**
 * Hook for restoring default key facts to an automation template.
 *
 * This mutation resets the key facts configuration of an automation template
 * back to the system defaults. Key facts are contact properties that automation
 * steps can reference when generating personalized communications.
 *
 * @param options - Configuration options for the mutation
 * @param options.onSuccess - Callback fired when the mutation succeeds
 * @param options.onError - Callback fired when the mutation fails
 * @returns A React Query mutation object
 */
export function useRestoreAutomationKeyFactsMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: void) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ id, impersonationContext }: { id: string; impersonationContext?: Api.IImpersonationContext }) => {
			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}/RestoreKeyFacts`,
					impersonationContext,
				}),
				'POST'
			);
		},
		onSuccess,
		onError,
	});
}
